import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ImagePlus, Megaphone, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { downscaleImage } from "@/lib/eventLib";

const MAX = 5;

export const AdminAds = ({ adminKey }) => {
  const [ads, setAds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sec, setSec] = useState("3");
  const fileRef = useRef(null);
  const target = useRef(null);
  const headers = { "X-Admin-Key": adminKey };

  const load = () => api.get("/public/ads").then((r) => setAds(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/public/ads-config").then((r) => setSec(String(r.data?.interval_sec ?? 3))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSec = async () => {
    const n = Number(sec);
    if (!(n >= 1 && n <= 60)) { toast.error("Timing 1 se 60 second ke beech rakhein"); return; }
    try { await api.put("/admin/ads-config", { interval_sec: n }, { headers }); toast.success(`Slide timing ${n} sec set ho gayi`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Save nahi hua"); }
  };

  const pick = (id) => { target.current = id; fileRef.current?.click(); };

  const onFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) { return; }
    if (!file.type.startsWith("image/")) { toast.error("Sirf image file chunein"); return; }
    setBusy(true);
    try {
      const image = await downscaleImage(file, 1600, 0.85);
      const id = target.current;
      if (id) {
        const ad = ads.find((a) => a.id === id);
        await api.put(`/admin/ads/${id}`, { image, link: ad?.link || "" }, { headers });
        toast.success("Ad banner change ho gaya");
      } else {
        await api.post("/admin/ads", { image, link: "" }, { headers });
        toast.success("Ad banner add ho gaya");
      }
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload nahi hua"); }
    setBusy(false);
  };

  const saveLink = async (a) => {
    try { await api.put(`/admin/ads/${a.id}`, { link: a.link }, { headers }); toast.success("Link save ho gaya"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Save nahi hua"); }
  };

  const del = async (a) => {
    if (!window.confirm("Ye ad slide delete karein?")) { return; }
    try { await api.delete(`/admin/ads/${a.id}`, { headers }); toast.success("Ad delete ho gaya"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete nahi hua"); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 shadow-sm space-y-3" data-testid="admin-ads">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="font-heading font-bold flex items-center gap-1.5"><Megaphone className="h-4 w-4 text-pink-600" /> Advertising Banner ({ads.length}/{MAX})</p>
          <p className="text-xs text-muted-foreground">Home screen par Upcoming Events ke upar aur login ke baad top par dikhega. Best size: 1500 x 500 (3:1).</p>
        </div>
        <div className="flex items-center gap-2" data-testid="admin-ad-timing">
          <span className="text-xs font-semibold text-slate-600">Slide Timing</span>
          <Input type="number" min={1} max={60} step={0.5} value={sec} onChange={(e) => setSec(e.target.value)} className="h-8 w-20 text-sm" data-testid="admin-ad-interval-input" />
          <span className="text-xs text-muted-foreground">sec</span>
          <Button size="sm" variant="outline" className="h-8" onClick={saveSec} data-testid="admin-ad-interval-save">Save</Button>
        </div>
        <Button size="sm" onClick={() => pick(null)} disabled={busy || ads.length >= MAX} className="gap-1.5 bg-pink-600 hover:bg-pink-700 text-white" data-testid="admin-ad-add-btn">
          <ImagePlus className="h-4 w-4" /> {busy ? "Uploading..." : "Add Slide"}
        </Button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="admin-ad-file-input" />
      {ads.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center" data-testid="admin-ads-empty">Abhi koi ad banner nahi hai.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {ads.map((a, k) => (
          <div key={a.id} className="border border-border rounded-lg overflow-hidden" data-testid={`admin-ad-${k}`}>
            <img src={a.image} alt={`Ad ${k + 1}`} className="w-full aspect-[3/1] object-cover bg-slate-100" />
            <div className="p-2 space-y-2">
              <Input value={a.link} onChange={(e) => setAds(ads.map((x) => (x.id === a.id ? { ...x, link: e.target.value } : x)))} onBlur={() => saveLink(a)} placeholder="Click link (optional) https://..." className="h-8 text-xs" data-testid={`admin-ad-link-${k}`} />
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1 gap-1 h-8" onClick={() => pick(a.id)} disabled={busy} data-testid={`admin-ad-change-${k}`}><RefreshCw className="h-3.5 w-3.5" /> Change</Button>
                <Button size="sm" variant="outline" className="flex-1 gap-1 h-8 text-red-600 hover:text-red-700" onClick={() => del(a)} data-testid={`admin-ad-delete-${k}`}><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
